import React, { useState, useEffect } from "react";
import { Form, Select, Input, Radio, InputNumber, Button, message } from "antd";
import apis from "../../apis";
import debounce from "lodash/debounce";
import "./ManualTopupPage.scss";

const ManualTopupPage = ({ mode, id, record, type, onBack, onSuccess }) => {
  const [form] = Form.useForm();
  const [topupPlanList, setTopupPlanList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [isCustom, setIsCustom] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detail, setDetail] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  const isEnterprise = mode === "enterprise";
  const isView = type === "view";

  useEffect(() => {
    if (isView) {
      if (id) {
        apis.getTopupOrderDetail({ order_id: id }).then((res) => {
          if (res.code === 200) {
            setDetail(res.data || {});
          }
        });
      }
      return;
    }
    form.resetFields();
    setIsCustom(false);
    // Pre-fetch plans
    apis
      .getTopupPlanList({
        topup_plan_type_code: isEnterprise
          ? "ENTERPRISE_EDITION"
          : "PERSONAL_EDITION",
        optional: true,
        page: 1,
        page_size: 9999,
      })
      .then((res) => {
        if (res.code === 200) {
          const data = (res.data?.data || []).map((_) => ({
            _id: _.topup_plan_id + "~~" + _?.latest_ver_info?.ver_no,
            ..._,
          }));

          setTopupPlanList(data);
        }
      })
      .catch(() => {
        // message.error('获取充值方案失败');
      });
  }, [isEnterprise, form, isView, id]);

  const displayData = { ...record, ...detail };

  const handleSearchCompany = React.useMemo(
    () =>
      debounce((value) => {
        if (!value) return;
        apis
          .getCompanyList({
            company_name: value,
            version_type: "ENTERPRISE,SUPER_GROUP,PRIVATE_DEPLOYMENT",
          })
          .then((res) => {
            if (res.code === 200) {
              setCompanyList(res.data || []);
            }
          })
          .catch(() => {
            setCompanyList([]);
          });
      }, 500),
    [],
  );

  const handleSearchUser = React.useMemo(
    () =>
      debounce((value) => {
        if (!value) return;
        apis
          .getUserListForTopup({
            user_name: value,
          })
          .then((res) => {
            if (res.code === 200) {
              setUserList(res.data || []);
            }
          })
          .catch(() => {
            setUserList([]);
          });
      }, 500),
    [],
  );

  const handlePlanChange = (planId) => {
    const plan = topupPlanList.find((p) => p._id === planId);
    if (plan) {
      setSelectedPlan(plan);
      form.setFieldsValue({
        equity_point: plan.equity_point,
        payment_amount:
          plan.discount_price !== undefined
            ? plan.discount_price
            : plan.original_price,
      });
    }
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const params = {
        payment_amount: values.payment_amount,
        payment_method_code: values.payment_method_code || "OFFLINE",
        // order_method_code and order_type are NOT required by API 9.2.5 Manual Topup
        // But we need co_id or user_id
      };

      if (isEnterprise) {
        params.co_id = values.co_id;
      } else {
        params.user_id = values.user_id;
      }

      if (isCustom) {
        params.is_custom = true;
        params.topup_plan_name = values.topup_plan_name;
        params.equity_point = values.equity_point;
      } else {
        params.topup_plan_id = selectedPlan.topup_plan_id;
        params.ver_no = selectedPlan?.latest_ver_info?.ver_no;
      }

      setSubmitLoading(true);
      apis
        .manualTopup(params)
        .then((res) => {
          setSubmitLoading(false);
          if (res.code === 200) {
            message.success("充值成功");
            onSuccess();
          } else {
            message.error(res.message);
          }
        })
        .catch(() => {
          setSubmitLoading(false);
        });
    });
  };

  return (
    <div className="manualTopupPage">
      <div className="content-body">
        <div className="itemTitle">
          <span className="backIconSpan" onClick={onBack}>
            <span className="titleLine"></span>
          </span>
          充值信息
        </div>

        <div className="itemTitle2">
          <span>
            01<i></i>
          </span>
          基本信息
        </div>

        <Form
          form={form}
          layout="horizontal"
          labelCol={{ flex: "120px" }}
          wrapperCol={{ flex: "auto" }}
          labelAlign="left"
          colon={false}
        >
          <div className="subscriptionItemsContent">
            {isEnterprise ? (
              <Form.Item
                name="co_id"
                label="企业"
                rules={[{ required: !isView, message: "请选择企业" }]}
              >
                {isView ? (
                  <span className="view-text">
                    {displayData.cust_name || displayData.co_name || "-"}
                  </span>
                ) : (
                  <Select
                    style={{ width: "100%" }}
                    showSearch
                    placeholder="请输入企业编码/名称查询"
                    onSearch={handleSearchCompany}
                    filterOption={false}
                    notFoundContent={null}
                    options={companyList.map((c) => ({
                      value: c.co_id,
                      label: (
                        <div className="PersonAndCompanySelectItem2">
                          <a
                            className="nameBox"
                            title={c.co_name}
                            style={{ textAlign: "left" }}
                          >
                            {c.co_name}
                          </a>
                          <span className="codeBox">{c.co_code}</span>
                        </div>
                      ),
                    }))}
                  />
                )}
              </Form.Item>
            ) : (
              <Form.Item
                name="user_id"
                label="用户手机号"
                rules={[{ required: !isView, message: "请选择用户" }]}
              >
                {isView ? (
                  <span className="view-text">
                    {displayData.cust_name ||
                      displayData.user_nickname ||
                      displayData.show_name ||
                      "-"}
                  </span>
                ) : (
                  <Select
                    style={{ width: "100%" }}
                    showSearch
                    placeholder="请输入手机号查询"
                    onSearch={handleSearchUser}
                    filterOption={false}
                    notFoundContent={null}
                    options={userList.map((u) => ({
                      value: u.user_id,
                      label: (
                        <div className="PersonAndCompanySelectItem2">
                          <span className="mobileBox" title={u.mobile_no}>
                            {u.mobile_no}
                          </span>
                          <span
                            className="nameBox"
                            title={u.show_name}
                            style={{ textAlign: "right" }}
                          >
                            {u.show_name}
                          </span>
                        </div>
                      ),
                    }))}
                  />
                )}
              </Form.Item>
            )}

            {!isView && (
              <Form.Item label="充值方案选择" required>
                <Radio.Group
                  value={isCustom ? "custom" : "plan"}
                  onChange={(e) => {
                    setIsCustom(e.target.value === "custom");
                    setSelectedPlan(null);
                    form.setFieldsValue({
                      topup_plan_id: undefined,
                      topup_plan_name: undefined,
                      equity_point: undefined,
                      payment_amount: undefined,
                    });
                  }}
                >
                  <Radio value="plan">选择已有方案</Radio>
                  <Radio value="custom">自定义</Radio>
                </Radio.Group>
              </Form.Item>
            )}

            {!isCustom && !isView ? (
              <Form.Item
                name="topup_plan_id"
                label="充值方案"
                rules={[{ required: true, message: "请选择充值方案" }]}
              >
                <Select
                  style={{ width: "100%" }}
                  placeholder="请选择充值方案"
                  onChange={handlePlanChange}
                  options={topupPlanList.map((p) => ({
                    value: p._id,
                    label: (
                      <span>
                        {p.topup_plan_name}{" "}
                        <span style={{ color: "#999", fontSize: "12px" }}>
                          ({p?.latest_ver_info?.ver_no})
                        </span>
                      </span>
                    ),
                  }))}
                />
              </Form.Item>
            ) : (
              (isCustom || isView) && (
                <Form.Item
                  name="topup_plan_name"
                  label="充值方案名称"
                  rules={[{ required: !isView, message: "请输入方案名称" }]}
                >
                  {isView ? (
                    <span className="view-text">
                      {displayData.order_name ||
                        displayData.topup_plan_name ||
                        "-"}
                    </span>
                  ) : (
                    <Input
                      style={{ width: "100%" }}
                      placeholder="请输入方案名称"
                    />
                  )}
                </Form.Item>
              )
            )}

            {!isCustom && selectedPlan && !isView ? (
              <Form.Item label="权益点">
                <span className="view-text">{selectedPlan.equity_point}</span>
              </Form.Item>
            ) : (
              (isCustom || isView) && (
                <Form.Item
                  name="equity_point"
                  label="权益点"
                  rules={[{ required: !isView, message: "请输入权益点" }]}
                >
                  {isView ? (
                    <span className="view-text">
                      {displayData.equity_point}
                    </span>
                  ) : (
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="请输入权益点"
                      min={0}
                      precision={0}
                    />
                  )}
                </Form.Item>
              )
            )}

            {!isCustom && selectedPlan && !isView && (
              <>
                <Form.Item label="原价 (元)">
                  <span className="view-text">
                    {selectedPlan.original_price !== undefined &&
                    selectedPlan.original_price !== null
                      ? Number(selectedPlan.original_price).toFixed(2)
                      : "-"}
                  </span>
                </Form.Item>
                <Form.Item label="折扣价 (元)">
                  <span className="view-text">
                    {selectedPlan.discount_price !== undefined &&
                    selectedPlan.discount_price !== null
                      ? Number(selectedPlan.discount_price).toFixed(2)
                      : "-"}
                  </span>
                </Form.Item>
                {(selectedPlan.topup_plan_desc || selectedPlan.description) && (
                  <Form.Item label="方案描述">
                    <span className="view-text">
                      {selectedPlan.topup_plan_desc || selectedPlan.description}
                    </span>
                  </Form.Item>
                )}
              </>
            )}

            <Form.Item
              name="payment_amount"
              label="支付金额 (元)"
              rules={[{ required: !isView, message: "请输入支付金额" }]}
            >
              {isView ? (
                <span className="view-text">
                  {displayData.payment_amount !== undefined
                    ? Number(displayData.payment_amount).toFixed(2)
                    : "-"}
                </span>
              ) : (
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="请输入支付金额"
                  min={0}
                  precision={2}
                />
              )}
            </Form.Item>

            {isView && (
              <>
                <Form.Item label="订单编号">
                  <span className="view-text">
                    {displayData.order_code || "-"}
                  </span>
                </Form.Item>
                <Form.Item label="订单状态">
                  <span className="view-text">
                    {(() => {
                      const status = displayData.order_status_code;
                      const isCancelled =
                        status === "CANCELLED" || status === "CANCELED";
                      return status === "COMPLETED"
                        ? "已完成"
                        : isCancelled
                          ? "已取消"
                          : status || "-";
                    })()}
                  </span>
                </Form.Item>
                <Form.Item label="支付方式">
                  <span className="view-text">
                    {(() => {
                      const method = displayData.payment_method_code;
                      const paymentMap = {
                        OFFLINE: "线下",
                        WECHAT: "微信支付",
                        WECHAT_PAY: "微信支付",
                        ALIPAY: "支付宝",
                      };
                      return paymentMap[method] || method || "-";
                    })()}
                  </span>
                </Form.Item>
                <Form.Item label="充值方式">
                  <span className="view-text">
                    {(() => {
                      const method = displayData.order_method_code;
                      const methodMap = {
                        MANUAL_TOPUP: "手工充值",
                        AUTO_TOPUP: "自动充值",
                        SELF_SERVICE_TOPUP: "自助充值",
                      };
                      return methodMap[method] || method || "-";
                    })()}
                  </span>
                </Form.Item>
                <Form.Item label="创建时间">
                  <span className="view-text">
                    {displayData.create_time || displayData.order_time || "-"}
                  </span>
                </Form.Item>
              </>
            )}
          </div>
        </Form>
      </div>

      <div className="footer-actions">
        {isView ? (
          <Button type="primary" onClick={onBack}>
            关闭
          </Button>
        ) : (
          <>
            <Button onClick={onBack}>取消</Button>
            <Button
              style={{ marginLeft: "24px" }}
              type="primary"
              loading={submitLoading}
              onClick={handleSubmit}
            >
              充值
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ManualTopupPage;
